import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { scaleFor } from '../../model/extendedMetrics';
import { formatPercent, formatSigned } from '../../model/format';
import { useMetricLabels } from '../../model/metricLabels';
import { computeTrendSeries, latestValues, TREND_METRICS } from '../../model/trends';
import type { YougovMonthlyRow } from '../../model/yougovData.schema';
import { AnalysisSelect, AnalysisSubHeader } from '../../../../components';
import { brandColor } from '../../../../utils/brandColor';
import { CHART_TOOLTIP_STYLE } from '../../../../utils/chartTooltipStyle';
import { formatMonth } from '../../../../utils/formatMonth';

interface MetricTrendsProps {
  rows: YougovMonthlyRow[];
  brands: string[];
}

const DEFAULT_METRIC = TREND_METRICS[0];

export const MetricTrends = ({ rows, brands }: MetricTrendsProps) => {
  const { t } = useTranslation('analysis');
  const [metric, setMetric] = useState(DEFAULT_METRIC);

  const metricLabels = useMetricLabels();
  const { points } = useMemo(
    () => computeTrendSeries(rows, metric, brands),
    [rows, metric, brands],
  );
  const ranked = useMemo(() => latestValues(points, brands), [points, brands]);
  const formatValue = scaleFor(metric) === 'percentage' ? formatPercent : formatSigned;
  const metricOptions = TREND_METRICS.map((option) => ({
    value: option,
    label: metricLabels[option],
  }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <AnalysisSubHeader
            title={t('analysisResult.marketingPerformance.metricTrends')}
            subtitle={t('analysisResult.marketingPerformance.metricTrendsSubtitle')}
            subtitleTone="meta"
          />
        </div>
        <AnalysisSelect
          variant="section"
          ariaLabel={t('analysisResult.marketingPerformance.metric')}
          value={metric}
          onValueChange={setMetric}
          options={metricOptions}
        />
      </div>
      <div className="flex items-stretch gap-4 rounded-xl border border-border-light bg-card p-4">
        <div className="relative min-w-0 flex-1">
          <ResponsiveContainer width="100%" height={380}>
            <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="month"
                tickFormatter={(month: string) => formatMonth(month, '2-digit')}
                tick={{ fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} width={36} />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE.contentStyle}
                isAnimationActive={false}
                labelFormatter={(month) =>
                  typeof month === 'string' ? formatMonth(month, 'numeric') : month
                }
                formatter={(value) => formatValue(typeof value === 'number' ? value : null)}
                labelStyle={{ ...CHART_TOOLTIP_STYLE.labelStyle, fontSize: 16, lineHeight: '24px' }}
              />
              {brands.map((brand) => (
                <Line
                  key={brand}
                  type="monotone"
                  dataKey={brand}
                  stroke={brandColor(brand, brands)}
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <ul
          aria-label={t('analysisResult.marketingPerformance.brandRanking')}
          className="flex w-[190px] shrink-0 flex-col gap-2 pt-1"
        >
          {ranked.map(({ brand, value }) => (
            <li
              key={brand}
              className="flex items-center gap-2.5 rounded-[10px] bg-surface px-3 py-2 dark:bg-surface-raised"
            >
              <span
                aria-hidden
                className="size-[9px] shrink-0 rounded-full"
                style={{ backgroundColor: brandColor(brand, brands) }}
              />
              <span className="text-sm font-bold text-foreground">{formatValue(value)}</span>
              <span className="truncate text-sm text-foreground-alt">{brand}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
